import { getGraphQlApiEndpoint, CACHE_LIFESPAN_IN_MILLISECONDS } from '../../constants/api'
import ApolloClient, { gql } from 'apollo-boost'
import {
  transformPlayer,
  transformMatchResults,
  transformCommonMatches,
  transformVsInfo,
  transformPlayers,
  transformCommonCompetitorMatches,
  transformResultCounts,
  transformPlayerStats,
  transformPsq,
  transformMatchUpBuckets,
  getFirstTournamentDate,
  transformSocialMatches,
  transformMatchUpForProfile,
  transformWatchlist,
  transformAppFeatures,
} from './data-transforms'
import {
  GetVsInfoQueryResponse,
  ManagePlayersInWatchlistsOperationResult,
  Paging,
  PersonIDEnum,
  PersonIDInput,
  Player,
  Provider,
  PublicPersonFilterOptions,
  SocialMatchUpUI,
  Sorting,
  UserStatus,
  WalkUpPersonDetails,
  Watchlist,
  WorldTennisNumber,
} from 'types/global'
import store, { getOwnTennisId } from 'redux/store'
import { DEFAULT_TOURNAMENTS_LOADED } from 'constants/profile'
import { getRayId, generateDateAtMidnight, hasCacheExpired } from 'utils'
import { MatchUpStatusEnum, MatchUpSubmissionStateEnum } from 'types/api/enums'
import {
  PersonAPIUpdateInput,
  PersonPSQInput,
  SelfSubmittedMatchUpInput,
  WatchlistInput,
} from 'types/api/types'
import auth from 'modules/auth'
import { setStoredRayId, setUserStatus } from 'actions/user-actions'
import { createRegLog } from 'modules/reg-logger'
import { GameType } from 'components/chart/ResultsDoughnutChart/ResultsDoughnutChartTypes'

class GraphQlApi {
  private client: ApolloClient<unknown>
  private _fetch: typeof window.fetch
  private _authenticate: boolean
  private pendingQueries: {
    [key: string]: AbortController
  }
  private cachedQueries: { [key: string]: Map<string, { timeCached: number; data: Player[] }> }
  private regFlowOperations: string[]

  constructor() {
    this._fetch = window.fetch.bind(window)
    this.client = new ApolloClient({
      fetch: async (input, options) => {
        return this._fetch(input, options).then(response => {
          const rayId = getRayId(response)

          if (rayId) store.dispatch(setStoredRayId(rayId))

          return response
        })
      },
      uri: getGraphQlApiEndpoint(),
      request: async ({ setContext }) => {
        if (this.authenticate) {
          const authToken = await auth.getAuthToken()
          setContext(() => ({ headers: { authorization: auth.makeAuthHeader(authToken) } }))
        }
      },
      onError: ({ operation: { getContext, operationName }, graphQLErrors }) => {
        const response = getContext()?.response

        if (response && this.shouldLogRayId(operationName))
          createRegLog(operationName, { rayId: getRayId(response) })

        const unauthenticated = graphQLErrors?.some(({ extensions }) => {
          return extensions?.code === 'UNAUTHENTICATED'
        })

        if (unauthenticated) store.dispatch(setUserStatus(UserStatus.UNAUTHENTICATED))
      },
    })
    this.pendingQueries = {}
    this.cachedQueries = { getPlayerSuggestions: new Map() }
    this._authenticate = false
    this.regFlowOperations = ['updateAccount', 'getRegistrationDetails', 'addAccount']
  }

  reset() {
    this.client.stop()
    this.client.resetStore()
  }

  get authenticate() {
    return this._authenticate
  }

  set authenticate(userState: boolean) {
    this._authenticate = userState
  }

  setPendingQuery(queryName: string, queryController: AbortController) {
    this.pendingQueries[queryName] = queryController
  }

  cancelPendingQuery(queryName) {
    const pendingQuery = this.pendingQueries[queryName]
    if (pendingQuery) {
      pendingQuery.abort()
      delete this.pendingQueries[queryName]
    }
  }

  private shouldLogRayId(opName: string) {
    return this.regFlowOperations.includes(opName)
  }

  addAccount(personInput: WalkUpPersonDetails, recaptchaInput) {
    return this.client
      .mutate({
        mutation: gql`
          mutation addAccount(
            $personInput: AddAccountPersonInput!
            $recaptchaInput: RecaptchaInput!
          ) {
            addAccountPerson(personInput: $personInput, recaptchaInput: $recaptchaInput) {
              registrationToken
              alreadyItfCustomer
              alreadyRegisteredForService
            }
          }
        `,
        variables: {
          personInput,
          recaptchaInput,
        },
      })
      .then(r => r.data.addAccountPerson)
  }

  getOwnProfile() {
    const id = {
      identifier: getOwnTennisId(),
      type: PersonIDEnum.TennisID,
    }

    return this.client
      .query({
        query: gql`
          query getOwnProfile($id: PersonIDInput!) {
            person(id: $id) {
              id
              nativeGivenName
              nativeFamilyName
              birthDate
              age
              birthYear
              tennisID
              nationalID
              sex
              nationalityCode
              optedOutOfPublicDataConsent
              isRanked
              eligibleForSelfSubmission
              eligibleForPsq
              biographicalInformation {
                residenceCountryCode
                placeOfResidence
                playingHand
                doublePlayingHand
                weightInKgs
                heightInMeters
                avatarUrl
                favouriteShot
              }
              worldTennisNumbers {
                type
                tennisNumber
                confidence
                gameZoneUpper
                gameZoneLower
              }
              matchUps {
                totalItems
              }
              hasSubmittedResult
            }
          }
        `,
        variables: {
          id,
        },
        fetchPolicy: 'no-cache',
      })
      .then(r => transformPlayer(r.data.person))
  }

  updateOwnProfile(input: PersonAPIUpdateInput) {
    const id = {
      identifier: getOwnTennisId(),
      type: PersonIDEnum.TennisID,
    }

    return this.client
      .mutate({
        mutation: gql`
          mutation updateOwnProfile($id: PersonIDInput!, $input: PersonUpdateInput!) {
            updatePerson(id: $id, input: $input) {
              id
              nativeGivenName
              nativeFamilyName
              birthDate
              age
              birthYear
              tennisID
              nationalID
              sex
              nationalityCode
              optedOutOfPublicDataConsent
              isRanked
              eligibleForSelfSubmission
              eligibleForPsq
              biographicalInformation {
                residenceCountryCode
                placeOfResidence
                playingHand
                doublePlayingHand
                weightInKgs
                heightInMeters
                avatarUrl
                favouriteShot
              }
              worldTennisNumbers {
                type
                tennisNumber
                confidence
                gameZoneUpper
                gameZoneLower
              }
              matchUps {
                totalItems
              }
              hasSubmittedResult
            }
          }
        `,
        variables: { id, input },
      })
      .then(r => transformPlayer(r.data.updatePerson))
  }

  getProfile({
    id,
    idType,
    includeProtectedFields = false,
  }: {
    id: string
    idType?: PersonIDEnum
    includeProtectedFields?: boolean
  }) {
    return this.client
      .query({
        query: gql`
          fragment PersonProtectedFields on Person {
            matchUps {
              totalItems
            }
            demographicStats {
              type
              strata {
                attribute
                label
              }
              percentileBounds
              stratumSize
            }
          }

          query getProfile($id: PersonIDInput!, $includeProtectedFields: Boolean!) {
            person(id: $id) {
              id
              nativeGivenName
              nativeFamilyName
              age
              birthYear
              birthDate
              tennisID
              nationalID
              sex
              nationalityCode
              optedOutOfPublicDataConsent
              isRanked
              eligibleForSelfSubmission
              eligibleForPsq
              biographicalInformation {
                residenceCountryCode
                placeOfResidence
                playingHand
                doublePlayingHand
                weightInKgs
                heightInMeters
                avatarUrl
                favouriteShot
              }
              worldTennisNumbers {
                type
                tennisNumber
                confidence
              }
              lastPlayedSingles
              lastPlayedDoubles
              hasSubmittedResult
              ...PersonProtectedFields @include(if: $includeProtectedFields)
            }
          }
        `,
        variables: {
          id: {
            identifier: id,
            type: idType || PersonIDEnum.ID,
          },
          includeProtectedFields,
        },
      })
      .then(r => transformPlayer(r.data.person))
  }

  getPlayerMatchResultListings(
    playerId: string,
    options: { skip: number; start: string; end: string }
  ) {
    const { skip, start, end } = options
    return this.client
      .query({
        query: gql`
          fragment SidePlayerInfo on Person {
            id
            nativeGivenName
            nativeFamilyName
            nationalityCode
            isRanked
            optedOutOfPublicDataConsent
          }
          query getTournamentAndResults(
            $matchUpFilter: MatchUpFilterOptions
            $tournamentsFilter: TournamentFilterOptions
            $pageArgs: PaginationArgs
            $id: PersonIDInput!
          ) {
            person(id: $id) {
              id
              allTournaments: tournaments(
                filter: $tournamentsFilter
                pageArgs: $pageArgs
                sort: { field: "start", direction: DESC }
              ) {
                items {
                  id
                  name
                  start
                  end
                  providerTournamentId
                  provider {
                    id
                    providerId
                    name
                    orgId
                    logoUrl
                    displayName
                    landingPageUrl
                    supportPageUrl
                  }
                  matchUps(filter: $matchUpFilter, sort: { field: "start", direction: DESC }) {
                    items {
                      id
                      start
                      end
                      worldTennisNumbers {
                        tennisNumber
                        confidence
                        personId
                        type
                      }
                      winningSide
                      score {
                        sets {
                          winnerGamesWon
                          loserGamesWon
                          tiebreaker {
                            loserPointsWon
                            winnerPointsWon
                          }
                        }
                        superTiebreak {
                          loserPointsWon
                          winnerPointsWon
                        }
                      }
                      status
                      sides {
                        players {
                          person {
                            ...SidePlayerInfo
                          }
                          playerNumber
                        }
                        sideNumber
                      }
                      type
                    }
                  }
                }
                totalItems
              }
              firstTournaments: tournaments(
                pageArgs: { limit: 5 }
                sort: { field: "start", direction: ASC }
              ) {
                items {
                  id
                  name
                  start
                }
              }
            }
          }
        `,
        variables: {
          id: { identifier: playerId, type: 'ID' },
          matchUpFilter: { personIds: { identifier: playerId, type: 'ID' } },
          tournamentsFilter: start && { start: { between: { start, end } } },
          pageArgs: {
            limit: DEFAULT_TOURNAMENTS_LOADED,
            skip,
          },
        },
      })
      .then(r => {
        const { allTournaments, firstTournaments } = r?.data?.person
        return {
          firstTournamentDate: getFirstTournamentDate(firstTournaments?.items),
          matchResultsListings: transformMatchResults(allTournaments, playerId),
        }
      })
  }

  getVsInfo(ids: [string, string]) {
    const [side1, side2] = ids
    return this.client
      .query({
        query: gql`
          query getVsInfo(
            $filter: MatchUpStatisticsFilterOptions!
            $side1: String!
            $side2: String!
            $p1: ID!
          ) {
            persons(
              filter: { ids: [{ identifier: $side1, type: ID }, { identifier: $side2, type: ID }] }
            ) {
              items {
                id
                nativeGivenName
                nativeFamilyName
                nationalityCode
                sex
                age
                birthYear
                biographicalInformation {
                  avatarUrl
                }
                worldTennisNumbers {
                  type
                  tennisNumber
                  confidence
                }
              }
              totalItems
            }
            matchUpStatistics(filter: $filter) {
              matchUpVsStatisticsCount {
                totalMatches
                wins(id: $p1)
                losses(id: $p1)
              }
              winLikelihood
            }
          }
        `,
        variables: {
          side1,
          side2,
          p1: side1,
          filter: {
            persons: { identifier: side1, type: 'ID' },
            opponents: { identifier: side2, type: 'ID' },
          },
        },
      })
      .then((r: { data: GetVsInfoQueryResponse }) => transformVsInfo(r.data, ids))
  }

  getCommonMatches(ids: string[], paging: Paging) {
    return this.client
      .query({
        query: gql`
          query getCommonMatches($side1: String!, $side2: String!, $paging: PaginationArgs) {
            matchUpStatistics(
              filter: {
                persons: [{ identifier: $side1, type: ID }, { identifier: $side2, type: ID }]
              }
            ) {
              # hardcoded sort and filter fields for now
              matchUps(
                pageArgs: $paging
                filter: { type: SINGLES }
                sort: { field: "start", direction: DESC }
              ) {
                items {
                  id
                  start
                  tournament {
                    id
                    name
                  }
                  winningSide
                  score {
                    sets {
                      winnerGamesWon
                      loserGamesWon
                      tiebreaker {
                        loserPointsWon
                        winnerPointsWon
                      }
                    }
                    superTiebreak {
                      loserPointsWon
                      winnerPointsWon
                    }
                  }
                  status
                  sides {
                    players {
                      person {
                        id
                        nationalityCode
                        nativeGivenName
                        nativeFamilyName
                        isRanked
                      }
                    }
                  }
                  worldTennisNumbers {
                    tennisNumber
                    personId
                    type
                  }
                }
                totalItems
              }
            }
          }
        `,
        variables: { side1: ids[0], side2: ids[1], paging: paging },
      })
      .then(r => ({
        count: r.data.matchUpStatistics.matchUps?.totalItems,
        matches: transformCommonMatches(r.data.matchUpStatistics.matchUps?.items),
      }))
  }

  getCommonCompetitorMatches(ids: string[], from?: string) {
    const [side1, side2] = ids

    return this.client
      .query({
        query: gql`
          query commonCompetitorMatches($p1: String!, $p2: String!, $start: DateOperators) {
            matchUpStatistics(
              filter: {
                persons: { identifier: $p1, type: ID }
                opponents: { identifier: $p2, type: ID }
                start: $start
              }
            ) {
              commonCompetitors {
                id
                nativeGivenName
                nativeFamilyName
                optedOutOfPublicDataConsent
                isRanked
                worldTennisNumbers {
                  type
                  tennisNumber
                }
                nationalityCode
                p1Vs: matchUpVsStatisticsCount(
                  filter: { opponents: { identifier: $p1, type: ID } }
                ) {
                  totalMatches
                  wins
                  losses
                }
                p2Vs: matchUpVsStatisticsCount(
                  filter: { opponents: { identifier: $p2, type: ID } }
                ) {
                  totalMatches
                  wins
                  losses
                }
              }
            }
          }
        `,
        variables: {
          p1: side1,
          p2: side2,
          start: from ? { after: from } : undefined,
        },
      })
      .then(r => transformCommonCompetitorMatches(r.data.matchUpStatistics.commonCompetitors))
  }

  resultsSummaryQuery(playerId: string, startDate: string, endDate: string) {
    const filter = {
      personId: {
        identifier: playerId,
        type: 'ID',
      },
      start: { between: { start: startDate, end: endDate } },
      statuses: [
        MatchUpStatusEnum.ABANDONED,
        MatchUpStatusEnum.COMPLETED,
        MatchUpStatusEnum.DEAD_RUBBER,
        MatchUpStatusEnum.DEFAULTED,
        MatchUpStatusEnum.DISQUALIFIED,
        MatchUpStatusEnum.RETIRED,
        MatchUpStatusEnum.SUSPENDED,
      ],
    }
    const filterSingles = { ...filter, type: 'SINGLES' }
    const filterDoubles = { ...filter, type: 'DOUBLES' }
    return this.client
      .query({
        query: gql`
          fragment ResultCountFields on AggregatedMatchUpCount {
            totalMatches
            matchesWon
            matchesLost
          }
          query getAggregatedResultCount(
            $filter: MatchUpAggregateFilterOptions!
            $filterSingles: MatchUpAggregateFilterOptions!
            $filterDoubles: MatchUpAggregateFilterOptions!
          ) {
            both: matchUpAggregatedCount(filter: $filter) {
              ...ResultCountFields
            }
            singles: matchUpAggregatedCount(filter: $filterSingles) {
              ...ResultCountFields
            }
            doubles: matchUpAggregatedCount(filter: $filterDoubles) {
              ...ResultCountFields
            }
          }
        `,
        variables: { filter, filterSingles, filterDoubles },
      })
      .then(r => transformResultCounts(r.data))
  }

  async ratingsHistoryQuery(options: {
    playerId: string
    start: string
    end: string
    limit: number
    skip: number
  }): Promise<{ items: WorldTennisNumber[]; totalItems: number }> {
    const { playerId, end, start, limit, skip } = options

    const res = await this.client.query({
      query: gql`
        query getRatings($id: String!, $to: DateTime!, $from: DateTime!, $limit: Int, $skip: Int) {
          worldTennisNumberHistory(
            filter: { ratingDate: { between: { start: $from, end: $to } } }
            sort: { field: "ratingDate", direction: ASC }
            id: { identifier: $id, type: ID }
            pageArgs: { limit: $limit, skip: $skip }
          ) {
            items {
              tennisNumber
              ratingDate
              type
            }
            totalItems
          }
        }
      `,
      variables: { id: playerId, to: end, from: start, limit, skip },
    })
    return res.data.worldTennisNumberHistory
  }

  async earliestRatingQuery(id: string): Promise<WorldTennisNumber[]> {
    const res = await this.client.query({
      query: gql`
        query getEarliestRating($id: String!) {
          worldTennisNumberHistory(
            pageArgs: { limit: 1, skip: 0 }
            sort: { field: "ratingDate", direction: ASC }
            id: { identifier: $id, type: ID }
          ) {
            items {
              tennisNumber
              ratingDate
              type
            }
          }
        }
      `,
      variables: { id: id },
    })
    return res.data.worldTennisNumberHistory.items
  }

  async playerSearch(filter: PublicPersonFilterOptions, paging: Paging, sort?: Sorting) {
    return this.client
      .query({
        query: gql`
          query getPlayers(
            $filter: PublicPersonFilterOptions
            $pageArgs: PaginationArgs
            $sort: SortOrder
          ) {
            publicPersons(filter: $filter, pageArgs: $pageArgs, sort: $sort) {
              items {
                id
                tennisID
                nativeGivenName
                nativeFamilyName
                nationalityCode
                sex
                age
                birthYear
                biographicalInformation {
                  avatarUrl
                }
                worldTennisNumbers {
                  type
                  tennisNumber
                  confidence
                }
                addresses {
                  addressType
                  city
                  countryCode
                  state
                }
              }
              totalItems
            }
          }
        `,
        variables: {
          filter,
          pageArgs: { ...paging },
          sort,
        },
      })
      .then(r => {
        return {
          results: transformPlayers(r.data && r.data.publicPersons.items),
          total: r.data.publicPersons.totalItems,
        }
      })
  }

  async getPlayerSuggestions(
    queryRef: string,
    filterOptions: PublicPersonFilterOptions,
    { limit }: Paging
  ) {
    const cacheKey = `${filterOptions.search?.term}.${limit}`
    const cache = this.cachedQueries.getPlayerSuggestions
    const { timeCached, data } = cache.get(cacheKey) || {}
    if (data && timeCached && !hasCacheExpired(timeCached, CACHE_LIFESPAN_IN_MILLISECONDS)) {
      return new Promise<Player[]>((resolve, reject) => {
        return resolve(data)
      })
    } else {
      const controller = new AbortController()
      this.setPendingQuery(queryRef, controller)

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      }

      if (this.authenticate) {
        const authToken = await auth.getAuthToken()
        headers.Authorization = auth.makeAuthHeader(authToken)
      }

      return fetch(getGraphQlApiEndpoint(), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query: `
            query getSuggestions(
              $filter: PublicPersonFilterOptions
              $pageArgs: PaginationArgs
              $sort: SortOrder
            ) {
              publicPersons(filter: $filter, pageArgs: $pageArgs, sort: $sort) {
                items {
                  id
                  tennisID
                  nativeGivenName
                  nativeFamilyName
                  nationalityCode
                  sex
                  age
                  birthYear
                  biographicalInformation {
                    avatarUrl
                  }
                  worldTennisNumbers {
                    type
                    tennisNumber
                    confidence
                  }
                  addresses {
                    addressType
                    city
                    countryCode
                    state
                  }
                }
                totalItems
              }
            }
          `,
          variables: {
            filter: filterOptions,
            pageArgs: { limit },
          },
        }),
        signal: controller.signal,
      })
        .then(res => res.json())
        .then(r => {
          const suggestions = transformPlayers(r.data && r.data.publicPersons.items)
          cache.set(cacheKey, { timeCached: Date.now(), data: suggestions })
          return suggestions
        })
    }
  }

  getPlayerStats(playerId: string) {
    const oneYearAgo = generateDateAtMidnight()
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)

    return this.client
      .query({
        query: gql`
          fragment MinMax on RatingMinMax {
            min
            max
          }
          fragment DoublesAndSingles on RatingBounds {
            doubles: boundsForType(ratingType: ITFDBL) {
              ...MinMax
            }
            singles: boundsForType(ratingType: ITF) {
              ...MinMax
            }
          }
          query getPlayerStats($oneYearAgo: DateTime!, $id: PersonIDInput!, $playerId: String!) {
            person(id: $id) {
              id
              lastPlayedSingles
              lastPlayedDoubles
              singlesInterval: matchUpIntervals(filter: { type: SINGLES }) {
                avgDays
              }
              doublesInterval: matchUpIntervals(filter: { type: DOUBLES }) {
                avgDays
              }
              wtnBoundsAllTime: ratingBounds {
                ...DoublesAndSingles
              }
              wtnBoundsThisYear: ratingBounds(dateFilter: { from: $oneYearAgo }) {
                ...DoublesAndSingles
              }
            }
            matchUpAggregatedCount(
              filter: { personId: { identifier: $playerId, type: ID }, type: SINGLES }
            ) {
              uniqueOpponents {
                count
              }
            }
          }
        `,
        variables: { oneYearAgo, id: { identifier: playerId, type: 'ID' }, playerId },
      })
      .then(r => transformPlayerStats(r.data))
  }

  getWatchlistWithPlayers(watchlistId: string): Promise<Watchlist> {
    return this.client
      .query({
        query: gql`
          query getWatchlistWithPlayers($id: ID!) {
            watchlist(id: $id) {
              id
              name
              persons {
                id
                nativeGivenName
                nativeFamilyName
                nationalityCode
                sex
                age
                birthYear
                addresses {
                  addressType
                  city
                  countryCode
                  state
                }
                biographicalInformation {
                  avatarUrl
                }
                worldTennisNumbers {
                  type
                  tennisNumber
                  confidence
                }
              }
              watchlistStats {
                minimumSinglesWorldTennisNumber
                maximumSinglesWorldTennisNumber
                meanSinglesWorldTennisNumber
                minimumDoublesWorldTennisNumber
                maximumDoublesWorldTennisNumber
                meanDoublesWorldTennisNumber
              }
            }
          }
        `,
        fetchPolicy: 'no-cache',
        variables: { id: watchlistId },
      })
      .then(r => transformWatchlist(r?.data?.watchlist))
  }

  getTournamentWinLoss(playerId: string, startFrom: string, startTo: string, matchType: GameType) {
    let mT
    if (matchType === 'SINGLES' || matchType === 'DOUBLES') {
      mT = [matchType.toUpperCase()]
    } else {
      mT = ['SINGLES', 'DOUBLES']
    }

    return this.client
      .query({
        query: gql`
          query getBucketMatchups(
            $id: PersonIDInput!
            $mT: [MatchUpTypeEnum!]!
            $startAfter: DateTime!
            $startBefore: DateTime!
            $statuses: [MatchUpStatusEnum!]
          ) {
            matchUpBucketedStats(
              filter: {
                personId: $id
                start: { between: { start: $startAfter, end: $startBefore } }
                type: $mT
                statuses: $statuses
              }
            ) {
              bucketsMeta {
                type
                matchUpStatsBuckets {
                  totalMatches
                  matchesWon
                  matchesLost
                  month
                  year
                }
              }
            }
          }
        `,
        variables: {
          startAfter: startFrom,
          startBefore: startTo,
          id: { type: 'ID', identifier: playerId },
          mT,
          statuses: [
            MatchUpStatusEnum.ABANDONED,
            MatchUpStatusEnum.COMPLETED,
            MatchUpStatusEnum.DEAD_RUBBER,
            MatchUpStatusEnum.DEFAULTED,
            MatchUpStatusEnum.DISQUALIFIED,
            MatchUpStatusEnum.RETIRED,
            MatchUpStatusEnum.SUSPENDED,
          ],
        },
      })
      .then(r => {
        return transformMatchUpBuckets(r.data.matchUpBucketedStats.bucketsMeta)
      })
  }

  submitPsq(id: PersonIDInput, input: PersonPSQInput) {
    return this.client
      .mutate({
        mutation: gql`
          mutation personSubmitPSQ($id: PersonIDInput!, $input: PersonPSQInput!) {
            personSubmitPSQ(id: $id, input: $input) {
              tennisNumber
              type
            }
          }
        `,
        variables: { id, input },
      })
      .then(r => transformPsq(r.data.personSubmitPSQ))
  }

  submitPsqPublic(input: PersonPSQInput) {
    return this.client
      .query({
        query: gql`
          query submitPublicPsq($input: PersonPSQInput!) {
            rmg(input: $input) {
              tennisNumber
              type
            }
          }
        `,
        variables: { input },
      })
      .then(r => transformPsq(r.data.rmg))
  }

  getAccountRegistrationDetails() {
    return this.client
      .query({
        query: gql`
          query getRegistrationDetails {
            accountPerson {
              birthDate
              countryOfResidenceCode
              nationalityCode
              nativeGivenName
              nativeFamilyName
              sex
              alreadyItfCustomer
              isCountryOfResidenceUpdateable
            }
          }
        `,
        fetchPolicy: 'no-cache',
      })
      .then(r => r.data.accountPerson)
  }

  getAccountDetails() {
    return this.client
      .query({
        query: gql`
          query getAccountDetails {
            accountPerson {
              isSexUpdateable
              isBirthDateUpdateable
              isNationalityUpdateable
              isNativeGivenNameUpdateable
              isNativeFamilyNameUpdateable
              isCountryOfResidenceUpdateable
              birthDate
              countryOfResidenceCode
              nationalityCode
              nativeGivenName
              nativeFamilyName
              sex
              biographicalInformation {
                avatarUrl
                doublePlayingHand
                favouriteShot
                heightInMeters
                playingHand
              }
              tennisId
            }
          }
        `,
        fetchPolicy: 'no-cache',
      })
      .then(r => r.data.accountPerson)
  }

  updateAccountDetails(personInput: object) {
    return this.client
      .mutate({
        mutation: gql`
          mutation updateAccount($personInput: UpdateAccountPersonInput!) {
            updateAccountPerson(personInput: $personInput) {
              nativeGivenName
              nativeFamilyName
              sex
              birthDate
              nationalityCode
              countryOfResidenceCode
              biographicalInformation {
                avatarUrl
                doublePlayingHand
                favouriteShot
                heightInMeters
                playingHand
              }
              tennisId
            }
          }
        `,
        variables: { personInput },
      })
      .then(r => r.data.updateAccountPerson)
  }

  getApprovedMatches(id: string, paging: Paging) {
    return this.client
      .query({
        query: gql`
          query getApprovedMatches($id: String!, $skip: Int, $limit: Int) {
            paginated: person(id: { identifier: $id, type: ID }) {
              id
              matchUps(
                filter: { selfSubmittedResult: { eq: true } }
                pageArgs: { limit: $limit, skip: $skip }
                sort: { field: "start", direction: DESC }
              ) {
                items {
                  id
                  start
                  end
                  createdAt
                  updatedAt
                  worldTennisNumbers {
                    confidence
                    tennisNumber
                    personId
                    type
                  }
                  winningSide
                  score {
                    superTiebreak {
                      winnerPointsWon
                      loserPointsWon
                    }
                    sets {
                      winnerGamesWon
                      loserGamesWon
                      tiebreaker {
                        loserPointsWon
                        winnerPointsWon
                      }
                    }
                  }
                  sides {
                    players {
                      person {
                        id
                        nativeGivenName
                        nativeFamilyName
                        nationalityCode
                        optedOutOfPublicDataConsent
                        biographicalInformation {
                          avatarUrl
                        }
                      }
                    }
                    sideNumber
                  }
                  type
                }
                totalItems
              }
            }
            recentlyUpdated: person(id: { identifier: $id, type: ID }) {
              id
              matchUps(
                filter: { selfSubmittedResult: { eq: true } }
                pageArgs: { limit: 4, skip: 0 }
                sort: { field: "updatedAt", direction: DESC }
              ) {
                items {
                  id
                  start
                  end
                  createdAt
                  updatedAt
                  worldTennisNumbers {
                    confidence
                    tennisNumber
                    personId
                    type
                  }
                  winningSide
                  score {
                    superTiebreak {
                      winnerPointsWon
                      loserPointsWon
                    }
                    sets {
                      winnerGamesWon
                      loserGamesWon
                      tiebreaker {
                        loserPointsWon
                        winnerPointsWon
                      }
                    }
                  }
                  sides {
                    players {
                      person {
                        id
                        nativeGivenName
                        nativeFamilyName
                        nationalityCode
                        optedOutOfPublicDataConsent
                        biographicalInformation {
                          avatarUrl
                        }
                        isRanked
                      }
                    }
                    sideNumber
                  }
                  type
                }
              }
            }
          }
        `,
        variables: { id, ...paging },
      })
      .then(r => ({
        paginatedMatches: transformSocialMatches(r.data.paginated.matchUps.items).map(match => {
          match.submittedStatus = MatchUpSubmissionStateEnum.APPROVED
          return match
        }),
        recentlyUpdatedMatches: transformSocialMatches(r.data.recentlyUpdated.matchUps.items).map(
          match => {
            match.submittedStatus = MatchUpSubmissionStateEnum.APPROVED
            return match
          }
        ),
        totals: {
          PENDING: 0,
          APPROVED: (r.data.paginated.matchUps.totalItems as number) || 0,
          REJECTED: 0,
        },
      }))
  }

  getOwnSocialMatches(state: MatchUpSubmissionStateEnum, paging: Paging) {
    return this.client
      .query({
        query: gql`
          query getOwnSocialMatches(
            $skip: Int
            $limit: Int
            $states: [MatchUpSubmissionStateEnum]
          ) {
            paginated: selfSubmittedMatchUps(
              pageArgs: { limit: $limit, skip: $skip }
              states: $states
              sort: { field: "start", direction: DESC }
            ) {
              items {
                id
                ownerId
                reviewedBy {
                  id
                }
                submissionState
                reviewedAt
                createdAt
                start
                end
                type
                worldTennisNumbers {
                  confidence
                  tennisNumber
                  personId
                  type
                }
                winningSide
                score {
                  superTiebreak {
                    winnerPointsWon
                    loserPointsWon
                  }
                  sets {
                    winnerGamesWon
                    loserGamesWon
                    tiebreaker {
                      loserPointsWon
                      winnerPointsWon
                    }
                  }
                }
                sides {
                  players {
                    person {
                      id
                      nativeGivenName
                      nativeFamilyName
                      nationalityCode
                      optedOutOfPublicDataConsent
                      biographicalInformation {
                        avatarUrl
                      }
                      isRanked
                    }
                  }
                  sideNumber
                }
                updatedAt
              }
              totalItems
              pendingItems
              approvedItems
              rejectedItems
            }
            recentlyUpdated: selfSubmittedMatchUps(
              pageArgs: { limit: 4, skip: 0 }
              sort: { field: "updatedAt", direction: DESC }
            ) {
              items {
                id
                ownerId
                reviewedBy {
                  id
                }
                submissionState
                reviewedAt
                createdAt
                start
                end
                type
                worldTennisNumbers {
                  confidence
                  tennisNumber
                  personId
                  type
                }
                winningSide
                score {
                  superTiebreak {
                    winnerPointsWon
                    loserPointsWon
                  }
                  sets {
                    winnerGamesWon
                    loserGamesWon
                    tiebreaker {
                      loserPointsWon
                      winnerPointsWon
                    }
                  }
                }
                sides {
                  players {
                    person {
                      id
                      nativeGivenName
                      nativeFamilyName
                      nationalityCode
                      optedOutOfPublicDataConsent
                      biographicalInformation {
                        avatarUrl
                      }
                      isRanked
                    }
                  }
                  sideNumber
                }
                updatedAt
              }
            }
          }
        `,
        variables: { states: [state], ...paging },
        fetchPolicy: 'network-only',
      })
      .then(r => ({
        paginatedMatches: transformSocialMatches(r.data.paginated.items),
        recentlyUpdatedMatches: transformSocialMatches(r.data.recentlyUpdated.items),
        totals: {
          PENDING: (r.data.paginated.pendingItems as number) || 0,
          APPROVED: (r.data.paginated.approvedItems as number) || 0,
          REJECTED: (r.data.paginated.rejectedItems as number) || 0,
        },
      }))
  }

  approveSocialMatch(id: string) {
    return this.client
      .mutate({
        mutation: gql`
          mutation approveSocialMatch($id: ID!) {
            approveSelfSubmittedMatchUp(id: $id) {
              id
              ownerId
              reviewedBy {
                id
              }
              submissionState
              createdAt
              reviewedAt
              start
              end
              type
              worldTennisNumbers {
                confidence
                tennisNumber
                personId
                type
              }
              winningSide
              score {
                superTiebreak {
                  winnerPointsWon
                  loserPointsWon
                }
                sets {
                  winnerGamesWon
                  loserGamesWon
                  tiebreaker {
                    loserPointsWon
                    winnerPointsWon
                  }
                }
              }
              sides {
                players {
                  person {
                    id
                    nativeGivenName
                    nativeFamilyName
                    nationalityCode
                    biographicalInformation {
                      avatarUrl
                    }
                    isRanked
                  }
                }
                sideNumber
              }
              updatedAt
            }
          }
        `,
        variables: { id },
      })
      .then(r => {
        const match = transformMatchUpForProfile<SocialMatchUpUI>(
          r.data.approveSelfSubmittedMatchUp
        )
        if (!match) throw new Error('received invalid matchUp object')
        return match
      })
  }

  rejectSocialMatch(id: string) {
    return this.client
      .mutate({
        mutation: gql`
          mutation rejectSocialMatch($id: ID!) {
            rejectSelfSubmittedMatchUp(id: $id) {
              id
              ownerId
              reviewedBy {
                id
              }
              submissionState
              createdAt
              reviewedAt
              start
              end
              type
              worldTennisNumbers {
                confidence
                tennisNumber
                personId
                type
              }
              winningSide
              score {
                superTiebreak {
                  winnerPointsWon
                  loserPointsWon
                }
                sets {
                  winnerGamesWon
                  loserGamesWon
                  tiebreaker {
                    loserPointsWon
                    winnerPointsWon
                  }
                }
              }
              sides {
                players {
                  person {
                    id
                    nativeGivenName
                    nativeFamilyName
                    nationalityCode
                    biographicalInformation {
                      avatarUrl
                    }
                    isRanked
                  }
                }
                sideNumber
              }
              updatedAt
            }
          }
        `,
        variables: { id },
      })
      .then(r => {
        const match = transformMatchUpForProfile<SocialMatchUpUI>(r.data.rejectSelfSubmittedMatchUp)
        if (!match) throw new Error('received invalid matchUp object')
        return match
      })
  }

  submitSocialMatch(input: SelfSubmittedMatchUpInput) {
    return this.client
      .mutate({
        mutation: gql`
          mutation submitSocialMatch($input: SelfSubmittedMatchUpInput!) {
            addSelfSubmittedMatchUp(input: $input) {
              id
              ownerId
              reviewedBy {
                id
              }
              submissionState
              createdAt
              reviewedAt
              start
              end
              type
              worldTennisNumbers {
                confidence
                tennisNumber
                personId
                type
              }
              winningSide
              score {
                superTiebreak {
                  winnerPointsWon
                  loserPointsWon
                }
                sets {
                  winnerGamesWon
                  loserGamesWon
                  tiebreaker {
                    loserPointsWon
                    winnerPointsWon
                  }
                }
              }
              sides {
                players {
                  person {
                    id
                    nativeGivenName
                    nativeFamilyName
                    nationalityCode
                    biographicalInformation {
                      avatarUrl
                    }
                    isRanked
                  }
                }
                sideNumber
              }
              updatedAt
            }
          }
        `,
        variables: {
          input,
        },
      })
      .then(r => {
        const match = transformMatchUpForProfile<SocialMatchUpUI>(r.data.addSelfSubmittedMatchUp)
        if (!match) throw new Error('received invalid matchUp object')
        return match
      })
  }

  deleteSocialMatch(id: string) {
    return this.client
      .mutate({
        mutation: gql`
          mutation deleteSocialMatch($id: ID!) {
            removeSelfSubmittedMatchUp(id: $id) {
              id
            }
          }
        `,
        variables: { id },
      })
      .then(r => r.data.removeSelfSubmittedMatchUp.id)
  }

  getWatchlists(): Promise<Watchlist[]> {
    return this.client
      .query({
        query: gql`
          query getWatchlists {
            watchlists {
              items {
                id
                personIds
                name
                watchlistStats {
                  minimumSinglesWorldTennisNumber
                  maximumSinglesWorldTennisNumber
                  meanSinglesWorldTennisNumber
                  minimumDoublesWorldTennisNumber
                  maximumDoublesWorldTennisNumber
                  meanDoublesWorldTennisNumber
                }
              }
              totalItems
            }
          }
        `,
        fetchPolicy: 'no-cache',
      })
      .then(r => (r?.data?.watchlists?.items ?? []).map(transformWatchlist))
  }

  addWatchlist(input: WatchlistInput) {
    return this.client
      .mutate({
        mutation: gql`
          mutation addWatchlist($input: WatchlistInput) {
            addWatchlist(input: $input) {
              id
              personIds
              name
            }
          }
        `,
        variables: {
          input,
        },
      })
      .then(r => {
        return transformWatchlist(r.data?.addWatchlist)
      })
  }

  getWatchlistsIdsForPlayer(id: string): Promise<string[]> {
    return this.client
      .query({
        query: gql`
          query getWatchlistIdsForPlayer($id: ID!) {
            watchlistIdsForPlayer(id: $id)
          }
        `,
        variables: {
          id,
        },
        fetchPolicy: 'no-cache',
      })
      .then(r => r.data.watchlistIdsForPlayer || [])
  }

  getProviderNames(): Promise<Provider[]> {
    return this.client
      .query({
        query: gql`
          query getProviderNames {
            providerNames {
              items {
                name
                displayName
              }
            }
          }
        `,
        variables: {},
        fetchPolicy: 'no-cache',
      })
      .then(r => r.data.providerNames.items || [])
  }

  addPlayersToWatchlists(watchlistIds: string[], playerIds: string[]): Promise<Watchlist[]> {
    return this.client
      .mutate({
        mutation: gql`
          mutation addPlayersToWatchlists($watchlistIds: [ID!]!, $personIds: [ID!]!) {
            addPersonsToWatchlists(watchlistIds: $watchlistIds, personIds: $personIds) {
              items {
                id
                name
              }
            }
          }
        `,
        variables: {
          watchlistIds: watchlistIds,
          personIds: playerIds,
        },
      })
      .then(r => (r.data?.addPersonsToWatchlists?.items ?? []).map(transformWatchlist))
  }

  removePlayersFromWatchlists(watchlistIds: string[], playerIds: string[]): Promise<Watchlist[]> {
    return this.client
      .mutate({
        mutation: gql`
          mutation removePlayersFromWatchlists($watchlistIds: [ID!]!, $personIds: [ID!]!) {
            removePersonsFromWatchlists(watchlistIds: $watchlistIds, personIds: $personIds) {
              items {
                id
              }
            }
          }
        `,
        variables: {
          watchlistIds: watchlistIds,
          personIds: playerIds,
        },
      })
      .then(r => (r.data?.removePersonsFromWatchlists?.items ?? []).map(transformWatchlist))
  }

  managePlayersInWatchlists(
    playerIds: string[],
    input: { addToIds?: string[]; removeFromIds?: string[]; createWatchlistName?: string }
  ): Promise<ManagePlayersInWatchlistsOperationResult[]> {
    const { addToIds, removeFromIds, createWatchlistName } = input
    let promises: Promise<any>[] = []

    const operationsPerformed: ManagePlayersInWatchlistsOperationResult[] = []

    if (addToIds?.length) {
      operationsPerformed.push({
        type: 'add',
        successful: false,
        updatedWatchlistsIds: null,
      })
      promises.push(this.addPlayersToWatchlists(addToIds, playerIds))
    }
    if (removeFromIds?.length) {
      operationsPerformed.push({
        type: 'remove',
        successful: false,
        updatedWatchlistsIds: null,
      })
      promises.push(this.removePlayersFromWatchlists(removeFromIds, playerIds))
    }
    if (createWatchlistName) {
      operationsPerformed.push({
        type: 'create',
        successful: false,
        updatedWatchlistsIds: null,
      })
      promises.push(this.addWatchlist({ name: createWatchlistName, personIds: playerIds }))
    }

    return Promise.allSettled(promises).then(results =>
      operationsPerformed.map((op, i) => {
        const result = results[i]
        if (result.status === 'rejected') return { ...op, error: result.reason }

        let updatedWatchlistsIds: string[] = []
        switch (op.type) {
          case 'add':
          case 'remove':
            updatedWatchlistsIds = result.value.map((wl: Watchlist) => wl.id) as string[]
            break
          case 'create':
            if (result.value?.id) updatedWatchlistsIds = [result.value.id] as string[]
            break
          default:
            break
        }

        return { ...op, successful: true, updatedWatchlistsIds }
      })
    )
  }

  renameWatchlist(id: string, name: string) {
    return this.client
      .mutate({
        mutation: gql`
          mutation renameWatchlist($id: ID!, $name: String!) {
            renameWatchlist(id: $id, name: $name) {
              id
              personIds
              name
            }
          }
        `,
        variables: {
          id,
          name,
        },
      })
      .then(r => transformWatchlist(r.data?.renameWatchlist))
  }

  removeWatchlist(id: string) {
    return this.client
      .mutate({
        mutation: gql`
          mutation removeWatchlist($id: ID!) {
            removeWatchlist(id: $id) {
              id
            }
          }
        `,
        variables: {
          id,
        },
      })
      .then(r => {
        if (!r.data?.removeWatchlist) throw new Error(`Failed to remove watchlist with id '${id}'`)
        return r.data.removeWatchlist.id
      })
  }

  getAppFeatures() {
    return this.client
      .query({
        query: gql`
          query getAppFeatures {
            features {
              walkUpRegistration
            }
          }
        `,
        fetchPolicy: 'no-cache',
      })
      .then(r => transformAppFeatures(r?.data?.features))
  }
}

const api = new GraphQlApi()

export default api
